/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useContext, useRef, useMemo} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {useThemeMode} from '../../../../../_fds/partials'
import {getCSS, getCSSVariableValue} from '../../../../../_fds/assets/ts/_utils'

interface ScheduleCompletionProps {
  chartKey: string
  completion: number
  strictlyUnacceptable: boolean
  day: string
  currentDay?: boolean
  // True when the day still has an unassigned (empty) shift — e.g. left over
  // from an applied template. Turns the day short-name label red.
  hasEmptyShift?: boolean
  // True while the week's data is still arriving. `completion` is meaningless
  // then, so the ring draws empty with an em dash instead of a number nobody
  // should act on.
  pending?: boolean
}

const ScheduleCompletion: React.FC<ScheduleCompletionProps> = ({
  chartKey,
  day,
  completion,
  currentDay,
  strictlyUnacceptable,
  hasEmptyShift,
  pending,
}: ScheduleCompletionProps) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartInstanceRef = useRef<ApexCharts | null>(null)
  const {mode} = useThemeMode()

  // Create/destroy the chart once per mount (and per theme switch — the
  // options bake in CSS-variable colors, so a mode change needs a re-read
  // from a re-created chart). Data/selection changes update in place below:
  // destroy+recreate on every prop change made a gauge click rebuild the DOM
  // of every gauge in the header.
  useEffect(() => {
    if (!chartRef.current) {
      return
    }
    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(completion, day, currentDay, strictlyUnacceptable, hasEmptyShift, pending)
    )
    chart.render()
    chartInstanceRef.current = chart

    return () => {
      chartInstanceRef.current = null
      chart.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, mode])

  useEffect(() => {
    const chart = chartInstanceRef.current
    if (!chart) {
      return
    }
    chart.updateOptions(
      getChartOptions(completion, day, currentDay, strictlyUnacceptable, hasEmptyShift, pending),
      false,
      false
    )
  }, [day, completion, currentDay, strictlyUnacceptable, hasEmptyShift, pending])
  const renderChat = () => {
    return (
      <div
        key={chartKey}
        ref={chartRef}
        id='daily-adherence-chart'
        className='px-1 d-flex align-items-center'
        // 89px keeps the completion bar at the height the sticky timeline
        // header offsets against (see styles.ts TableGridTimeline top calc).
        style={{cursor: 'pointer', height: '89px'}}
      />
    )
  }
  const chart = useMemo(() => {
    return renderChat()
  }, [chartRef])
  return chart
}

export {ScheduleCompletion}

export function getChartOptions(
  completion: number,
  day: string,
  currentDay?: boolean,
  strictlyUnacceptable?: boolean,
  hasEmptyShift?: boolean,
  pending?: boolean
): ApexOptions {
  // An unassigned (empty) shift left on the day wins: the short-name label goes
  // red to flag that template-applied shifts still need staffing.
  const labelColor = hasEmptyShift
    ? getCSSVariableValue('--bs-danger')
    : currentDay
    ? getCSSVariableValue('--bs-primary')
    : getCSSVariableValue('--bs-gray-800')
  var fillColor = getCSSVariableValue('--bs-danger')
  if (completion > 80) {
    fillColor = getCSSVariableValue('--bs-success')
  } else if (completion > 60) {
    fillColor = getCSSVariableValue('--bs-warning')
  }
  // Waiting on data: an empty ring in the track colour, so nothing reads as a
  // red (or green) verdict on a week nobody has scored yet.
  if (pending) {
    fillColor = getCSSVariableValue('--bs-gray-300') || getCSSVariableValue('--bs-secondary')
  }
  var options: ApexOptions = {
    chart: {
      height: 110,
      type: 'radialBar',
      // These gauges are destroyed and recreated on every data wave while the
      // page loads (shifts, hours, allowable hours each arrive separately) —
      // replaying the ~1s sweep animation each time makes the header appear
      // "one ring at a time". Render instantly instead.
      animations: {
        enabled: false,
      },
      // The ring diameter follows the chart width in sparkline mode; 78px is
      // the largest ring that still fits the 89px container row (which the
      // sticky timeline header offsets against — see styles.ts).
      width: 78,
      // ApexCharts >=3.45 sizes radialBar from the chart width and reserves
      // label padding that crops a narrow ring; sparkline mode drops the
      // padding so the donut renders centered and uncropped again.
      sparkline: {
        enabled: true,
      },
    },
    series: [pending ? 0 : completion],
    plotOptions: {
      radialBar: {
        hollow: {
          margin: 5,
          // A wider hollow gives the day/percent labels room — at 40% of a
          // small ring the text overflowed the hole.
          size: '58%',
        },
        track: {
          // --bs-secondary-dark is not an emitted CSS variable (the dark theme
          // maps $secondary-dark onto --bs-secondary), so fall back to a real
          // one — an empty color makes ApexCharts log "undefined color".
          background: currentDay
            ? getCSSVariableValue('--bs-gray-400') || getCSSVariableValue('--bs-secondary')
            : getCSSVariableValue('--bs-secondary'),
        },
        dataLabels: {
          name: {
            offsetY: -4,
            show: true,
            color: labelColor,
            fontSize: '10px',
          },
          value: {
            color: strictlyUnacceptable
              ? getCSSVariableValue('--bs-danger')
              : pending
              ? getCSSVariableValue('--bs-gray-500')
              : getCSSVariableValue('--bs-gray-800'),
            offsetY: -5,
            fontSize: '12px',
            show: true,
            // Always supplied, never left to the ApexCharts default:
            // updateOptions deep-MERGES into the live options, so a formatter
            // set once while pending would survive into the loaded state and
            // freeze every gauge at "—".
            formatter: pending ? () => '—' : (val: number) => `${Math.round(val)}%`,
          },
        },
      },
    },
    stroke: {
      lineCap: 'round',
    },
    fill: {
      colors: [fillColor],
    },
    labels: [day],
  }

  return options
}
